import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Paper, 
  Text, 
  Container, 
  Grid, 
  Button, 
  Image, 
  Badge, 
  Tabs,
  Group,
  Modal,
  ActionIcon,
  Flex,
  Box,
  Stack,
  Title,
  Textarea,
  Center,
  UnstyledButton,
  TextInput
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconDownload, IconEdit } from '@tabler/icons-react';

import { getUserFullDetails } from '../Redux/user/slice';
import { DropzoneButton } from './DropzoneButton';
import { IntlProvider, FormattedMessage } from "react-intl";

export default function MyPersonalSpace() {
  // User info and Redux setup
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  const userFullDetails = useSelector((state) => state.user.userFullDetails);
  const dispatch = useDispatch();
  
  // State for copy and profile presentation
  const [copySuccess, setCopySuccess] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // Profile Presentation State
  const [title, setTitle] = useState('Palm Oil & Cocoa Exporter');
  const [subtitle, setSubtitle] = useState('Sustainable Agricultural Exports from Brazil');
  const [editTitleModal, setEditTitleModal] = useState(false);
  const [editSubtitleModal, setEditSubtitleModal] = useState(false);
  const [documents, setDocuments] = useState([
    { 
      id: 1, 
      name: 'EUDR Compliance Report Q1 2024', 
      url: `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/eudr-report-q1-2024.pdf` 
    },
    { 
      id: 2, 
      name: 'Traceability Certificate - Palm Oil', 
      url: `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/palm-oil-traceability.pdf` 
    },
    { 
      id: 3, 
      name: 'Cocoa Origin Verification', 
      url: `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/cocoa-origin.pdf` 
    }
  ]);
  
  // Edit document link state
  const [editDocumentModal, setEditDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDocumentURL, setEditDocumentURL] = useState('');

  // Fetch user details on component mount
  useEffect(() => {
    if (!userFullDetails.userId) {
      dispatch(getUserFullDetails({ email: userinfo.email }));
    }
  }, [dispatch, userinfo.email, userFullDetails.userId]);

  // Copy Public Key to clipboard
  const copyPublicKey = () => {
    navigator.clipboard.writeText(userFullDetails.PublicKey);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Download QR Code handler
  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/profile_qr.png`;
    link.download = 'profile_qr.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Document download handler
  const handleDocumentDownload = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Navigate to document page handler
  const navigateToDocument = (url) => {
    window.open(url, '_blank');
  };
  
  // Open edit document modal
  const openEditDocumentModal = (doc) => {
    setEditingDocument(doc);
    setEditDocumentURL(doc.url);
    setEditDocumentModal(true);
  };
  
  // Save document URL changes
  const saveDocumentURL = () => {
    if (editingDocument) {
      const updatedDocuments = documents.map(doc => 
        doc.id === editingDocument.id 
          ? { ...doc, url: editDocumentURL } 
          : doc
      );
      setDocuments(updatedDocuments);
    }
    setEditDocumentModal(false);
  };

  return (
    <Container size="xl">
      <Paper padding="md" shadow="xs">
        <Tabs defaultValue="userInfo">
          <Tabs.List>
            <Tabs.Tab value="userInfo">
              <FormattedMessage id="tabs.userInfo" defaultMessage="User Information" />
            </Tabs.Tab>
            <Tabs.Tab value="profilePresentation">
              <FormattedMessage id="tabs.profilePresentation" defaultMessage="Profile Presentation" />
            </Tabs.Tab>
          </Tabs.List>

          {/* User Information Tab */}
          <Tabs.Panel value="userInfo" pt="xs">
            <Center>
              <Paper style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                maxWidth: '1000px', 
                width: '100%' 
              }}>
                <Center style={{ flex: 1, padding: '20px' }}>
                  <Image
                    src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/p.png`}
                    fallbackSrc={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/p.avif`}
                    alt="User Profile Image"
                    width={200}
                    height={200}
                    fit="contain"
                    withPlaceholder
                  />
                </Center>
                <div style={{ flex: 2, padding: '20px' }}>
                  <Grid gutter="md">
                    <div style={{ marginBottom: '20px' }}>
                      <Text size="xl" weight={700}>
                        <FormattedMessage id="userprofile.title" defaultMessage="User Profile" />
                      </Text>
                    </div>
                    
                    {[
                      { label: 'userInfo.name', value: userFullDetails.userName },
                      { label: 'userInfo.email', value: userFullDetails.email },
                      { label: 'userInfo.country', value: userFullDetails.Country },
                      { label: 'userInfo.acctID.profile', value: userFullDetails.accountId },
                      { label: 'userInfo.prodcontractID', value: userFullDetails.product_contractId },
                      { label: 'userInfo.comercontractID', value: userFullDetails.commercial_contractId },
                    ].map((detail) => (
                      <div 
                        key={detail.label} 
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginBottom: '15px' 
                        }}
                      >
                        <Text weight={500} mr="md">
                          <FormattedMessage id={detail.label} />
                        </Text>
                        <Text>{detail.value}</Text>
                      </div>
                    ))}

                    {/* Public Key Section */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                      <Text weight={500} mr="md">
                        <FormattedMessage id="userInfo.pubKey" defaultMessage="Public Key" />
                      </Text>
                      <Button
                        color="blue"
                        size="xs"
                        onClick={copyPublicKey}
                        disabled={copySuccess}
                      >
                        {copySuccess 
                          ? <FormattedMessage id="button.copy.success" defaultMessage="Copied" /> 
                          : <FormattedMessage id="button.copy" defaultMessage="Copy" />
                        }
                      </Button>
                    </div>

                    {/* Hedera Address Section */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                      <Text weight={500} mr="md">
                        <FormattedMessage id="userInfo.hederaAddress" defaultMessage="Hedera Address" />
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text>{userFullDetails.accountId}</Text>
                        <Button
                          component="a"
                          href={`https://hashscan.io/${userFullDetails.userName?.includes('#') ? 'testnet' : 'mainnet'}/account/${userFullDetails.accountId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          variant="subtle"
                          color="blue"
                        >
                          HashScan
                        </Button>
                      </div>
                    </div>

                    {/* Balance Section */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                      <Text weight={500} mr="md">
                        <FormattedMessage id="userInfo.balance" defaultMessage="Balance" />
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text>1,260</Text>
                        <Badge size="lg" color="green" variant="light">
                          ℏ
                        </Badge>
                      </div>
                    </div>

                    <Button 
                      color="blue" 
                      variant="outline" 
                      fullWidth 
                      style={{ marginTop: '20px' }}
                    >
                      <FormattedMessage id="button.editprofile" defaultMessage="Edit Profile" />
                    </Button>
                  </Grid>
                </div>
              </Paper>
            </Center>

            {/* Dropzone Component for User Info Tab */}
            <Center style={{ marginTop: '20px' }}>
              <DropzoneButton />
            </Center>
          </Tabs.Panel>

          {/* Profile Presentation Tab */}
          <Tabs.Panel value="profilePresentation" pt="xs">
            {/* Centered QR Code Section */}
            <Center mb="xl">
              <Flex 
                direction="column" 
                align="center" 
                justify="center"
              >
                <Image
                  src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/profile_qr.png`}
                  alt="Profile QR Code"
                  width={150}
                  height={150}
                  withPlaceholder
                />
                <ActionIcon 
                  variant="light" 
                  color="blue" 
                  onClick={downloadQRCode}
                  title="Download QR Code"
                  mt="md"
                >
                  <IconDownload size="1.125rem" />
                </ActionIcon>
              </Flex>
            </Center>

            {/* Profile Presentation Section - A4 Format */}
            <Center>
            <Box 
                style={{ 
                  position: 'relative', 
                  width: '595px', /* A4 width in pixels (8.27 × 11.69 inches) */
                  height: '842px', /* A4 height in pixels */
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '40px',
                  border: '1px solid rgba(0,0,0,0.2)', /* Added border */
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)' /* Optional: adds subtle shadow */
                }}
              >
                {/* Background Image with repeat pattern */}
                <div
                  style={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%',
                    backgroundImage: `url(https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/background.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    zIndex: 1,
                    opacity: 0.7
                  }}
                />

                {/* Content Overlay - Made Transparent */}
                <Box 
                  style={{ 
                    position: 'relative', 
                    zIndex: 2, 
                    padding: '30px',
                    backgroundColor: 'transparent',
                    textAlign: 'center',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  {/* Title Section */}
                  <Title 
                    order={2} 
                    style={{ 
                      position: 'relative',
                      display: 'inline-block',
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      padding: '10px 20px',
                      borderRadius: '25px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      marginBottom: '20px'
                    }}
                  >
                    {title}
                    <ActionIcon 
                      variant="subtle" 
                      color="blue" 
                      onClick={() => setEditTitleModal(true)}
                      style={{ 
                        position: 'absolute', 
                        top: '50%', 
                        right: '-40px', 
                        transform: 'translateY(-50%)' 
                      }}
                    >
                      <IconEdit size="1rem" />
                    </ActionIcon>
                  </Title>

                  {/* Subtitle Section */}
                  <Text 
                    size="lg" 
                    color="dimmed"
                    style={{ 
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      display: 'inline-block',
                      position: 'relative',
                      marginBottom: '60px'
                    }}
                  >
                    {subtitle}
                    <ActionIcon 
                      variant="subtle" 
                      color="blue" 
                      onClick={() => setEditSubtitleModal(true)}
                      style={{ 
                        position: 'absolute', 
                        top: '50%', 
                        right: '-40px', 
                        transform: 'translateY(-50%)' 
                      }}
                    >
                      <IconEdit size="1rem" />
                    </ActionIcon>
                  </Text>

                  {/* Documents List - Transparent box with no border */}
                  <Box 
                    style={{ 
                      width: '80%',
                      padding: '20px',
                      backgroundColor: 'transparent',
                      borderRadius: '10px',
                      marginTop: '20px'
                    }}
                  >
                    <Title order={4} style={{ marginBottom: '20px', textAlign: 'center' }}>
                      Documents
                    </Title>
                    <Stack spacing="xs" align="center">
                      {documents.map((doc) => (
                        <Paper 
                          key={doc.id}
                          withBorder 
                          p="sm" 
                          style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            borderRadius: '8px',
                            width: '100%',
                            marginBottom: '5px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                          }}
                        >
                          {/* Clickable document name text */}
                          <UnstyledButton 
                            onClick={() => navigateToDocument(doc.url)}
                            style={{ 
                              flex: 1, 
                              textAlign: 'left',
                              cursor: 'pointer'
                            }}
                          >
                            <Text weight={500} style={{ color: '#1E88E5' }}>{doc.name}</Text>
                          </UnstyledButton>
                          
                          {/* Edit link button only */}
                          <ActionIcon 
                            variant="subtle" 
                            color="gray"
                            onClick={() => openEditDocumentModal(doc)}
                            title="Edit document link"
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Center>

            {/* Edit Title Modal */}
            <Modal
              opened={editTitleModal}
              onClose={() => setEditTitleModal(false)}
              title="Edit Title"
            >
              <Textarea
                value={title}
                onChange={(event) => setTitle(event.currentTarget.value)}
                placeholder="Enter your profile title"
                label="Profile Title"
                minRows={2}
              />
              <Group position="right" mt="md">
                <Button onClick={() => setEditTitleModal(false)}>Save</Button>
              </Group>
            </Modal>

            {/* Edit Subtitle Modal */}
            <Modal
              opened={editSubtitleModal}
              onClose={() => setEditSubtitleModal(false)}
              title="Edit Subtitle"
            >
              <Textarea
                value={subtitle}
                onChange={(event) => setSubtitle(event.currentTarget.value)}
                placeholder="Enter your profile subtitle"
                label="Profile Subtitle"
                minRows={2}
              />
              <Group position="right" mt="md">
                <Button onClick={() => setEditSubtitleModal(false)}>Save</Button>
              </Group>
            </Modal>
            
            {/* Edit Document URL Modal */}
            <Modal
              opened={editDocumentModal}
              onClose={() => setEditDocumentModal(false)}
              title={`Edit Link: ${editingDocument?.name || ''}`}
            >
              <TextInput
                value={editDocumentURL}
                onChange={(event) => setEditDocumentURL(event.currentTarget.value)}
                placeholder="Enter document URL"
                label="Document URL"
              />
              <Group position="right" mt="md">
                <Button variant="outline" onClick={() => setEditDocumentModal(false)} mr="xs">
                  Cancel
                </Button>
                <Button onClick={saveDocumentURL}>
                  Save
                </Button>
              </Group>
            </Modal>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Container>
  );
}